import { albumHref } from '@/features/artists/artistHref'
import { ImportPanel } from "./ImportPanel"
import { pageClassName } from '@/components/ui/Page'
import { ErrorNotice } from '@/components/ui/Feedback'
import { Heart, Music, Search, SearchX } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router";

import {
  useLibrary,
  useShuffleLibrary,
  useStorage,
  type LibraryFilters,
} from "@/api/hooks";
import type { LibraryAlbum } from "@/api/types";
import { toQueueItem, usePlayer } from "@/audio/store";
import {
  Button,
  EmptyState,
  Skeleton,
  toast,
  TrackListSkeleton,
} from "@/components/ui";
import { PlaylistsPage } from "@/features/playlists/PlaylistsPage";
import { formatBytes } from "@/lib/format";
import { usePersistentState } from "@/lib/persistentState";

import { errorMessage } from "./errorMessage";
import { LibraryAlbumGrid } from "./LibraryAlbumGrid";
import { LibrarySelectionBar } from "./LibrarySelectionBar";
import {
  LIBRARY_PANEL_ID,
  LibraryFilterField,
  LibraryToolbar,
  LibraryActions,
  libraryTabId,
  LIBRARY_SORTS,
  LIBRARY_TABS,
  type LibrarySort,
  type LibraryTab,
} from "./LibraryToolbar";
import { LibraryTrackList } from "./LibraryTrackList";
import { SelectAllBar } from "./SelectAllBar";
import { useDebouncedValue } from "./useDebouncedValue";
import { useSelectionMode } from "./useSelectionMode";

export function LibraryPage() {
  const navigate = useNavigate();

  // Remembered across navigation: someone who browses by album should not have
  // to pick "Albums" every time they come back to the library.
  const [savedTab, setTab] = usePersistentState<LibraryTab>(
    "library.tab",
    LIBRARY_TABS,
    "songs",
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view");
  const tab: LibraryTab = LIBRARY_TABS.includes(view as LibraryTab)
    ? view as LibraryTab
    : savedTab;
  // Give the remembered view a URL too, so Back restores the original tab.
  useEffect(() => {
    if (view !== tab) {
      const params = new URLSearchParams(searchParams);
      params.set("view", tab);
      setSearchParams(params, { replace: true });
    }
    setTab(tab);
  }, [view, tab, searchParams, setSearchParams, setTab]);
  const [sort, setSort] = usePersistentState<LibrarySort>(
    "library.sort",
    LIBRARY_SORTS,
    "recent",
  );
  const [queryInput, setQueryInput] = useState("");
  const query = useDebouncedValue(queryInput.trim(), 300);

  const {
    selection,
    active: selecting,
    start: startSelecting,
    exit: exitSelection,
  } = useSelectionMode();

  const shuffle = useShuffleLibrary();
  const playQueue = usePlayer((state) => state.playQueue);

  const filters = useMemo<LibraryFilters>(
    () => ({
      q: query || undefined,
      favorite: tab === "favorites" ? true : undefined,
      sort,
    }),
    [query, tab, sort],
  );

  const library = useLibrary(filters);
  const storage = useStorage();

  const items = useMemo(
    () => library.data?.pages.flatMap((page) => page.results) ?? [],
    [library.data],
  );

  const ids = useMemo(() => items.map((item) => item.id), [items]);

  // Changing what the list contains invalidates a selection made against the
  // old one, so each control drops it as it changes rather than leaving the user
  // holding rows they can no longer see.
  function changeTab(next: LibraryTab) {
    exitSelection();
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set("view", next);
    setSearchParams(params);
  }

  function changeQuery(next: string) {
    exitSelection();
    setQueryInput(next);
  }

  function changeSort(next: LibrarySort) {
    exitSelection();
    setSort(next);
  }

  function openAlbumDetail(album: LibraryAlbum) {
    exitSelection();
    void navigate(albumHref(album));
  }

  /**
   * Shuffle the whole library and start playing.
   *
   * The order comes from the server, not from `items`: the list is paginated, so
   * shuffling what is loaded would only ever draw from the first page. Playback
   * then runs straight down that already-random queue with the player's own
   * shuffle left off, so the queue you see is the order you get.
   */
  async function shuffleAndPlay() {
    exitSelection();
    try {
      const { results } = await shuffle.mutateAsync({
        q: query || undefined,
        favorite: tab === "favorites" || undefined,
      });
      const queue = results.map(toQueueItem).filter((entry) => entry !== null);

      if (queue.length === 0) {
        toast.info(
          "Nothing to shuffle yet",
          tab === "favorites"
            ? "Favourite a few songs first."
            : "Add some music and it will be ready to play.",
        );
        return;
      }

      playQueue(queue, 0, { startFrom: "anywhere", source: "shuffle" });
      toast.success(
        `Shuffling ${queue.length} ${queue.length === 1 ? "song" : "songs"}`,
      );
    } catch (thrown) {
      toast.error("Could not start shuffle", errorMessage(thrown));
    }
  }

  // ---------------------------------------------------------------------
  // Infinite scroll
  // ---------------------------------------------------------------------

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = library;
  const listVisible = tab === "songs" || tab === "favorites";

  useEffect(() => {
    const node = sentinelRef.current;
    if (!listVisible || !node || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) void fetchNextPage();
      },
      // Start the next page before the user reaches the bottom, so the list
      // rarely stalls under the docked player.
      { rootMargin: "400px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, listVisible]);

  // Browser back/forward can change the view without clicking a tab.
  useEffect(() => {
    exitSelection();
  }, [tab, exitSelection]);

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  const usage = storage.data;
  const matchCount = library.data?.pages[0]?.count;
  const filtering = query.length > 0 || tab === "favorites";

  function renderLayout(actions: ReactNode, content: ReactNode) {
    return (
      <div className={pageClassName('browse')}>
        <header className="mb-4">
          <div className="flex min-h-11 items-center justify-between gap-3">
            <h1 className="text-2xl font-semibold tracking-tight text-text">Library</h1>
            <div className="flex items-center gap-2">
              <ImportPanel />
              {actions}
            </div>
          </div>
          {/* Fixed height so the summary landing does not push the list down. */}
          <div className="mt-1 flex h-5 items-center">
            {storage.isPending ? (
              <Skeleton className="h-3.5 w-44" />
            ) : usage ? (
              <p className="text-sm text-text-muted">
                {usage.track_count.toLocaleString()}{" "}
                {usage.track_count === 1 ? "song" : "songs"} ·{" "}
                {formatBytes(usage.used_bytes)}
                {usage.quota_bytes ? ` of ${formatBytes(usage.quota_bytes)}` : ""}
                {listVisible && filtering && typeof matchCount === "number"
                  ? ` · ${matchCount.toLocaleString()} shown`
                  : ""}
              </p>
            ) : null}
          </div>
        </header>

        {/* Categories stay reachable while scrolling, below the phone's safe area. */}
        <div className="top-safe sticky z-20 -mx-4 bg-surface-sunken px-4 pb-2 pt-3">
          <LibraryToolbar tab={tab} onTabChange={changeTab} />
        </div>

        {/* Filtering scrolls away; categories remain pinned above it. */}
        {tab !== "playlists" ? (
          <div className="mt-3">
            <LibraryFilterField
              tab={tab}
              query={queryInput}
              onQueryChange={changeQuery}
            />
          </div>
        ) : null}
        <div
          id={LIBRARY_PANEL_ID}
          role="tabpanel"
          aria-labelledby={libraryTabId(tab)}
          className="mt-4"
        >
          {content}
        </div>

        {listVisible && selecting ? (
          <>
            <LibrarySelectionBar
              items={items}
              selection={selection}
              onExit={exitSelection}
            />
            {/* Room for the docked bar, so the last row is never hidden by it. */}
            <div aria-hidden="true" className="h-16" />
          </>
        ) : null}
      </div>
    );
  }

  if (tab === "playlists") return <PlaylistsPage renderLayout={renderLayout} />;

  return renderLayout(
    <LibraryActions
      tab={tab}
      sort={sort}
      onSortChange={changeSort}
      onShuffle={shuffleAndPlay}
      shuffling={shuffle.isPending}
      // Nothing downloaded means nothing to play; the empty state already
      // explains what to do about it.
      shuffleDisabled={(usage?.track_count ?? 0) === 0}
      onStartSelection={items.length > 0 ? startSelecting : undefined}
      selectionActive={selecting}
    />,
    <>
      {tab === "albums" ? (
        <LibraryAlbumGrid
          search={query}
          onOpen={openAlbumDetail}
          onClearSearch={() => changeQuery("")}
        />
      ) : library.isPending ? (
        <TrackListSkeleton count={8} />
      ) : library.isError ? (
        <ErrorNotice title="Could not load your library" busy={library.isFetching} onRetry={() => void library.refetch()}>
          {errorMessage(library.error)}
        </ErrorNotice>
      ) : items.length === 0 ? (
        query ? (
          <EmptyState
            icon={SearchX}
            title={`Nothing matches “${query}”`}
            description="Try a different word, or search your music sources for something new to add."
            action={{ label: "Clear filter", onClick: () => changeQuery("") }}
          />
        ) : tab === "favorites" ? (
          <EmptyState
            icon={Heart}
            title="No favourites yet"
            description="Add a song to favourites from its menu and it will appear here."
            action={{
              label: "Browse songs",
              onClick: () => changeTab("songs"),
            }}
          />
        ) : (
          <EmptyState
            icon={Music}
            title="Your library is empty"
              description="Add songs to your library to keep them permanently."
            action={{
              label: "Search for music",
              icon: Search,
              onClick: () => {
                void navigate("/search");
              },
            }}
          />
        )
      ) : (
        <>
          {selecting ? (
            <SelectAllBar
              ids={ids}
              selection={selection}
              hasMore={hasNextPage}
            />
          ) : null}

          <LibraryTrackList
            items={items}
            selection={selection}
            selecting={selecting}
          />

          {hasNextPage ? (
            <div className="flex flex-col items-center gap-2 py-6">
              {/* The observer target sits above the button so the fallback is
                  only ever reached by someone who scrolled past it. */}
              <div
                ref={sentinelRef}
                aria-hidden="true"
                className="h-px w-full"
              />
              <Button
                variant="secondary"
                loading={isFetchingNextPage}
                onClick={() => void fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : (
            <div className="h-6" />
          )}
        </>
      )}
    </>,
  );
}
